// src/features/analytics/useAnalyticsMediaStream.ts
//
// Per-stream page queries (dashboard ADR-009; api ADR-0015). All account
// scope only, ranged one-shot (polled only while a broadcast is live), enabled only while the gateway
// declares `features.hourly` and the page has a cid + mediaId. Kept in one
// module: the six hooks share one shape and one gating rule, and the page is
// their only consumer.

import { useQuery } from '@tanstack/react-query';
import { AnalyticsApi, type EventWindowRange } from './AnalyticsApi';
import { ANALYTICS_QUERY_KEYS, BROADCASTS_LIVE_REFETCH_MS, type AnalyticsInterval } from '@/lib/constants/analytics';
import { anyBroadcastInProgress } from './broadcasts';
import type { UtcRange } from '@/shared/utils/utcRange';
import type {
  AnalyticsEnvelope,
  BroadcastsData,
  ConcurrencyData,
  HeatmapData,
  PlaybackDailyData,
  PlaybackHourlyData,
  PlaybackWindowData,
} from './types';

export interface MediaQueryResult<T> {
  data: AnalyticsEnvelope<T> | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

function shape<T>(query: {
  data: AnalyticsEnvelope<T> | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => unknown;
}): MediaQueryResult<T> {
  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}

/** The event report for one stream over an explicit window (≤ 92 days). */
export function useAnalyticsMediaWindow(
  enabled: boolean,
  cid: number | null,
  mediaId: string | null,
  window: EventWindowRange
): MediaQueryResult<PlaybackWindowData> {
  return shape(
    useQuery({
      queryKey: ANALYTICS_QUERY_KEYS.mediaPlaybackWindow(cid ?? 0, mediaId ?? '', window.from, window.to),
      queryFn: () => AnalyticsApi.getMediaPlaybackWindow(cid!, mediaId!, window),
      enabled: enabled && cid !== null && mediaId !== null,
      staleTime: 60_000,
    })
  );
}

/** Hour-grain trend for one stream (game-length windows). */
export function useAnalyticsMediaHourly(
  enabled: boolean,
  cid: number | null,
  mediaId: string | null,
  window: EventWindowRange
): MediaQueryResult<PlaybackHourlyData> {
  return shape(
    useQuery({
      queryKey: ANALYTICS_QUERY_KEYS.mediaPlaybackHourly(cid ?? 0, mediaId ?? '', window.from, window.to),
      queryFn: () => AnalyticsApi.getMediaPlaybackHourly(cid!, mediaId!, window),
      enabled: enabled && cid !== null && mediaId !== null,
      staleTime: 60_000,
    })
  );
}

/** Day-grain trend for one stream (UTC days, or local days with tz). */
export function useAnalyticsMediaDaily(
  enabled: boolean,
  cid: number | null,
  mediaId: string | null,
  range: UtcRange,
  tz?: string
): MediaQueryResult<PlaybackDailyData> {
  return shape(
    useQuery({
      queryKey: ANALYTICS_QUERY_KEYS.mediaPlaybackDaily(cid ?? 0, mediaId ?? '', range.from, range.to, tz ?? 'utc'),
      queryFn: () => AnalyticsApi.getMediaPlaybackDaily(cid!, mediaId!, range, tz),
      enabled: enabled && cid !== null && mediaId !== null,
      staleTime: 60_000,
    })
  );
}

/** Concurrency series for one stream (presence, ≤ 90 days; caller clamps). */
export function useAnalyticsMediaConcurrency(
  enabled: boolean,
  cid: number | null,
  mediaId: string | null,
  range: EventWindowRange,
  interval: AnalyticsInterval
): MediaQueryResult<ConcurrencyData> {
  return shape(
    useQuery({
      queryKey: ANALYTICS_QUERY_KEYS.mediaConcurrency(cid ?? 0, mediaId ?? '', range.from, range.to, interval),
      queryFn: () => AnalyticsApi.getMediaConcurrency(cid!, mediaId!, range, interval),
      enabled: enabled && cid !== null && mediaId !== null,
      staleTime: 60_000,
    })
  );
}

/**
 * Broadcast windows for one stream over the presence retention (caller passes
 * ≤ 90 days, floored to the product window, with an hour-stable `to` so the
 * key does not churn per render). Never polled — except while a run is in
 * progress, when the list refreshes every minute so the current broadcast's
 * end and peak keep moving.
 */
export function useAnalyticsMediaBroadcasts(
  enabled: boolean,
  cid: number | null,
  mediaId: string | null,
  range: EventWindowRange
): MediaQueryResult<BroadcastsData> {
  return shape(
    useQuery({
      queryKey: ANALYTICS_QUERY_KEYS.mediaBroadcasts(cid ?? 0, mediaId ?? '', range.from, range.to),
      queryFn: () => AnalyticsApi.getMediaBroadcasts(cid!, mediaId!, range),
      enabled: enabled && cid !== null && mediaId !== null,
      staleTime: 5 * 60 * 1000,
      refetchInterval: (query) =>
        anyBroadcastInProgress(query.state.data?.data.rows, Math.floor(Date.now() / 1000))
          ? BROADCASTS_LIVE_REFETCH_MS
          : false,
    })
  );
}

/** By-media weekday/hour heatmap (route 9, always available; caller clamps to 90 days). */
export function useAnalyticsHeatmapByMedia(
  cid: number | null,
  mediaId: string | null,
  range: UtcRange
): MediaQueryResult<HeatmapData> {
  return shape(
    useQuery({
      queryKey: ANALYTICS_QUERY_KEYS.heatmapByMedia(cid ?? 0, mediaId ?? '', range.from, range.to),
      queryFn: () => AnalyticsApi.getHeatmapByMedia(cid!, mediaId!, range),
      enabled: cid !== null && mediaId !== null,
      staleTime: 5 * 60 * 1000,
    })
  );
}
